import type { BaseTile, TileWithPosition } from "./types";

const GRID_COLS = 3;
const MAX_ROWS = 20;
const MAX_EXPANSION_ITERATIONS = 10;
const UNPLACED_POSITION = -1;

type GridCell = string | null;
type Grid = GridCell[][];

const isCellOccupied = (grid: Grid, row: number, col: number): boolean =>
  grid[row]?.[col] != null;

const isRangeAvailable = (
  grid: Grid,
  startRow: number,
  startCol: number,
  colSpan: number,
  rowSpan: number,
): boolean => {
  if (startRow + rowSpan > MAX_ROWS || startCol + colSpan > GRID_COLS) {
    return false;
  }

  for (let row = startRow; row < startRow + rowSpan; row++) {
    for (let col = startCol; col < startCol + colSpan; col++) {
      if (col >= GRID_COLS || isCellOccupied(grid, row, col)) {
        return false;
      }
    }
  }

  return true;
};

const occupyCells = (
  grid: Grid,
  startRow: number,
  startCol: number,
  colSpan: number,
  rowSpan: number,
  id: string,
): void => {
  for (let row = startRow; row < startRow + rowSpan; row++) {
    for (let col = startCol; col < startCol + colSpan; col++) {
      grid[row][col] = id;
    }
  }
};

type Direction = "column" | "row";

interface ExpansionConfig {
  canExpand: (
    grid: Grid,
    tile: TileWithPosition,
    maxUsedBoundary: number,
  ) => boolean;
  expand: (grid: Grid, tile: TileWithPosition) => void;
  incrementSpan: (tile: TileWithPosition) => void;
}

const createExpansionConfig = (
  direction: Direction,
  maxUsedCol: number,
): ExpansionConfig => {
  if (direction === "column") {
    return {
      canExpand: (grid, tile, maxUsedBoundary) => {
        const newCol = tile.col + tile.colSpan;
        if (newCol >= GRID_COLS) return false;

        for (let row = tile.row; row < tile.row + tile.rowSpan; row++) {
          if (row >= maxUsedBoundary || isCellOccupied(grid, row, newCol)) {
            return false;
          }
        }
        return true;
      },
      expand: (grid, tile) => {
        const newCol = tile.col + tile.colSpan;
        for (let row = tile.row; row < tile.row + tile.rowSpan; row++) {
          grid[row][newCol] = tile.id;
        }
      },
      incrementSpan: tile => {
        tile.colSpan++;
      },
    };
  }
  return {
    canExpand: (grid, tile, maxUsedBoundary) => {
      const newRow = tile.row + tile.rowSpan;
      if (newRow >= maxUsedBoundary) return false;

      for (let col = tile.col; col < tile.col + tile.colSpan; col++) {
        if (col >= maxUsedCol || isCellOccupied(grid, newRow, col)) {
          return false;
        }
      }
      return true;
    },
    expand: (grid, tile) => {
      const newRow = tile.row + tile.rowSpan;
      for (let col = tile.col; col < tile.col + tile.colSpan; col++) {
        grid[newRow][col] = tile.id;
      }
    },
    incrementSpan: tile => {
      tile.rowSpan++;
    },
  };
};

const tryExpand = (
  grid: Grid,
  tile: TileWithPosition,
  config: ExpansionConfig,
  maxUsedBoundary: number,
): boolean => {
  let hasExpanded = false;

  while (config.canExpand(grid, tile, maxUsedBoundary)) {
    config.expand(grid, tile);
    config.incrementSpan(tile);
    hasExpanded = true;
  }

  return hasExpanded;
};

const createInitialGrid = (): Grid =>
  Array.from({ length: MAX_ROWS }, () => Array<GridCell>(GRID_COLS).fill(null));

const initializeTileWithPosition = (tile: BaseTile): TileWithPosition => ({
  ...tile,
  colSpan: tile.baseColSpan,
  rowSpan: tile.baseRowSpan,
  row: UNPLACED_POSITION,
  col: UNPLACED_POSITION,
  originalColSpan: tile.baseColSpan,
  originalRowSpan: tile.baseRowSpan,
});

const placeTileOnGrid = (grid: Grid, tile: TileWithPosition): boolean => {
  for (let row = 0; row < MAX_ROWS; row++) {
    for (let col = 0; col < GRID_COLS; col++) {
      if (isRangeAvailable(grid, row, col, tile.colSpan, tile.rowSpan)) {
        tile.row = row;
        tile.col = col;
        occupyCells(grid, row, col, tile.colSpan, tile.rowSpan, tile.id);
        return true;
      }
    }
  }
  return false;
};

const isTilePlaced = (tile: TileWithPosition): boolean =>
  tile.row !== UNPLACED_POSITION && tile.col !== UNPLACED_POSITION;

const expandTilesIteratively = (
  grid: Grid,
  tiles: TileWithPosition[],
  maxUsedRow: number,
): void => {
  const maxUsedCol = GRID_COLS;
  let hasChanges = true;
  let iterations = 0;

  while (hasChanges && iterations < MAX_EXPANSION_ITERATIONS) {
    hasChanges = false;
    iterations++;

    for (const tile of tiles) {
      if (!isTilePlaced(tile)) continue;

      const columnConfig = createExpansionConfig("column", maxUsedCol);
      if (tryExpand(grid, tile, columnConfig, maxUsedRow)) {
        hasChanges = true;
      }

      const rowConfig = createExpansionConfig("row", maxUsedCol);
      if (tryExpand(grid, tile, rowConfig, maxUsedRow)) {
        hasChanges = true;
      }
    }
  }
};

export const calculateOptimalSpans = (
  enabledTiles: BaseTile[],
): TileWithPosition[] => {
  const grid = createInitialGrid();
  const tilesWithPositions = enabledTiles.map(initializeTileWithPosition);

  for (const tile of tilesWithPositions) {
    placeTileOnGrid(grid, tile);
  }

  const maxUsedRow = Math.max(
    ...tilesWithPositions.map(t => t.row + t.rowSpan),
    0,
  );

  expandTilesIteratively(grid, tilesWithPositions, maxUsedRow);

  return tilesWithPositions;
};
